"use client"

import { useEffect, useState } from "react"
import API from "../services/api" // Assuming API service is in this path
import { io } from "socket.io-client"
import { ArrowLeft, Facebook, Instagram, ChevronLeft, ChevronRight } from "lucide-react"

const socket = io("https://server-deploy-aq8t.onrender.com")

// Translation system
const translations = {
  fr: {
    // Header
    back: "Retour",
    // Welcome banner
    spa: "BALANCE ",
    wellness: "BY NOVOTEL",
    discoverSpa: "Détendez-vous et ressourcez-vous dans notre espace bien-être luxueux",
    // Loading & Empty states
    loadingSpas: "Chargement des spas...",
    noSpasFound: "Aucun spa trouvé",
    comeBackSoonSpas: "Revenez bientôt pour découvrir nos espaces bien-être",
    // Spa details
    backToList: "Retour à la liste",
    noDescription: "Pas de description disponible.",
    defaultFeatures: {
      massage: "Massages relaxants",
      bodyCare: "Soins du corps",
      aromatherapy: "Aromathérapie",
    },
    // Footer
    contact: "Contact",
    address: "Adresse",
    reservations: "Réservations",
    wifi: "Wi-Fi",
    followUs: "Suivez-nous",
    network: "Réseau",
    password: "Mot de passe",
    availableAtReception: "Disponible à la réception",
    addressLine1: "Avenue Mohamed V",
    addressLine2: "Tunis, Tunisie",
    allRightsReserved: "Tous droits réservés",
    createdBy: "Créé par",
  },
  en: {
    // Header
    back: "Back",
    // Welcome banner
    spa: "BALANCE ",
    wellness: "BY NOVOTEL",
    discoverSpa: "Relax and rejuvenate in our luxurious wellness area",
    // Loading & Empty states
    loadingSpas: "Loading spas...",
    noSpasFound: "No spas found",
    comeBackSoonSpas: "Come back soon to discover our wellness spaces",
    // Spa details
    backToList: "Back to list",
    noDescription: "No description available.",
    defaultFeatures: {
      massage: "Relaxing massages",
      bodyCare: "Body treatments",
      aromatherapy: "Aromatherapy",
    },
    // Footer
    contact: "Contact",
    address: "Address",
    reservations: "Reservations",
    wifi: "Wi-Fi",
    followUs: "Follow Us",
    network: "Network",
    password: "Password",
    availableAtReception: "Available at reception",
    addressLine1: "Avenue Mohamed V",
    addressLine2: "1002 Tunis, Tunisia",
    allRightsReserved: "All rights reserved",
    createdBy: "Created by",
  },
  ar: {
    // Header
    back: "رجوع",
    // Welcome banner
    spa: "سبا",
    wellness: "وعافية",
    discoverSpa: "استرخِ وجدد نشاطك في منطقة العافية الفاخرة لدينا",
    // Loading & Empty states
    loadingSpas: "جاري تحميل السبا...",
    noSpasFound: "لم يتم العثور على سبا",
    comeBackSoonSpas: "عد قريبًا لاكتشاف مساحات العافية لدينا",
    // Spa details
    backToList: "العودة إلى القائمة",
    noDescription: "لا يوجد وصف متاح.",
    defaultFeatures: {
      massage: "تدليك مريح",
      bodyCare: "علاجات الجسم",
      aromatherapy: "العلاج بالروائح",
    },
    // Footer
    contact: "اتصل بنا",
    address: "العنوان",
    reservations: "الحجوزات",
    wifi: "واي فاي",
    followUs: "تابعونا",
    network: "الشبكة",
    password: "كلمة المرور",
    availableAtReception: "متوفرة في الاستقبال",
    addressLine1: "شارع الكورنيش",
    addressLine2: "تونس، تونس",
    allRightsReserved: "جميع الحقوق محفوظة",
    createdBy: "تم إنشاؤه بواسطة",
  },
}

const languages = [
  { code: "fr", name: "Français", flag: "/images/fr-flag-v2.png" },
  { code: "en", name: "English", flag: "/images/en-flag-v2.png" },
  { code: "ar", name: "العربية", flag: "/images/ar-flag-v2.png" },
]

const SpasClient = () => {
  const [spas, setSpas] = useState([])
  const [isLoaded, setIsLoaded] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [currentLanguage, setCurrentLanguage] = useState("fr")
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false)
  const [currentSlide, setCurrentSlide] = useState(0)

  const spaImages = [
    "/images/spa/spa1.png",
    "/images/spa/spa2.png",
    "/images/spa/spa3.png",
    "/images/spa/spa4.png",
    "/images/spa/spa5.png",
    "/images/spa/spa6.png",
    "/images/spa/spa7.png",
    "/images/spa/spa8.png",
    "/images/spa/spa9.png",
    "/images/spa/spa10.png",
  ]

  // Get translation function
  const t = (key) => translations[currentLanguage][key] || translations.fr[key] || key

  const fetchSpas = async () => {
    try {
      setIsLoading(true)
      const res = await API.get("/spas")
      setSpas(res.data)
      setIsLoaded(true)
    } catch (error) {
      console.error("Erreur chargement des spas:", error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchSpas()
    // Force a re-render after a short delay to ensure visibility
    const timer = setTimeout(() => {
      setIsLoaded(true)
    }, 100)
    socket.on("spaCreated", (newSpa) => {
      setSpas((prev) => [...prev, newSpa])
    })
    socket.on("spaUpdated", (updatedSpa) => {
      setSpas((prev) => prev.map((s) => (s._id === updatedSpa._id ? updatedSpa : s)))
    })
    socket.on("spaDeleted", (deletedId) => {
      setSpas((prev) => prev.filter((s) => s._id !== deletedId))
    })
    return () => {
      socket.off("spaCreated")
      socket.off("spaUpdated")
      socket.off("spaDeleted")
      clearTimeout(timer)
    }
  }, [])

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Language loading and saving
  useEffect(() => {
    const savedLanguage = localStorage.getItem("novotel-language")
    if (savedLanguage && translations[savedLanguage]) {
      setCurrentLanguage(savedLanguage)
      document.documentElement.dir = savedLanguage === "ar" ? "rtl" : "ltr"
      document.documentElement.lang = savedLanguage
    }
  }, [])

  // Language change handler
  const changeLanguage = (langCode) => {
    setCurrentLanguage(langCode)
    localStorage.setItem("novotel-language", langCode)
    setShowLanguageDropdown(false)
    document.documentElement.dir = langCode === "ar" ? "rtl" : "ltr"
    document.documentElement.lang = langCode
  }

  const getCurrentLanguage = () => languages.find((lang) => lang.code === currentLanguage)

  useEffect(() => {
    const slideInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % spaImages.length)
    }, 4000) // Change slide every 4 seconds

    return () => clearInterval(slideInterval)
  }, [spaImages.length])

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % spaImages.length)
  }

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + spaImages.length) % spaImages.length)
  }

  const goToSlide = (index) => {
    setCurrentSlide(index)
  }

  return (
    <div className={`hotel-app0 ${currentLanguage === "ar" ? "rtl" : "ltr"}`}>
      <style jsx>{`
        /* Language dropdown styles */
        .language-selector {
          position: absolute;
          top: 15px;
          left: 15px;
          z-index: 20;
        }

        .language-toggle {
          display: flex;
          align-items: center;
          gap: 8px;
          background: rgba(0, 0, 0, 0.7);
          color: white;
          border: none;
          border-radius: 20px;
          padding: 8px 12px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.3s ease;
          backdrop-filter: blur(10px);
        }

        .language-toggle:hover {
          background: rgba(0, 0, 0, 0.8);
          transform: scale(1.05);
        }

        .language-flag {
          width: 20px;
          height: 15px;
          object-fit: cover;
          border-radius: 2px;
        }

        .language-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          background: rgba(0, 0, 0, 0.9);
          border-radius: 8px;
          padding: 8px 0;
          min-width: 150px;
          z-index: 1000;
          backdrop-filter: blur(10px);
          margin-top: 5px;
        }

        .language-option {
          display: flex;
          align-items: center;
          padding: 8px 16px;
          cursor: pointer;
          transition: background-color 0.2s;
          gap: 8px;
          color: white;
          font-size: 14px;
        }

        .language-option:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        .language-option.active {
          background: rgba(255, 255, 255, 0.2);
        }

        .flag-small {
          width: 20px;
          height: 15px;
          object-fit: cover;
          border-radius: 2px;
        }

        /* Enhanced main container with subtle background and better spacing */
        .app-main {
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          min-height: 100vh;
          padding: 20px 0;
        }

        .content-container {
          max-width: 800px;
          margin: 0 auto;
          padding: 0 16px;
        }

        /* Enhanced spa container with subtle card styling */
        .spa-container {
          margin-bottom: 48px;
          background: white;
          border-radius: 16px;
          padding: 32px 24px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          border: 1px solid #e2e8f0;
          transition: all 0.3s ease;
        }

        .spa-container:hover {
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
          transform: translateY(-2px);
        }

        /* Enhanced spa name with better typography */
        .spa-name {
          font-size: 28px;
          font-weight: 800;
          color: #1e40af;
          margin-bottom: 12px;
          text-align: center;
          letter-spacing: -0.025em;
          line-height: 1.2;
        }

        /* Enhanced spa description with better styling */
        .spa-description {
          color: #475569;
          text-align: center;
          margin-bottom: 40px;
          font-size: 16px;
          line-height: 1.6;
          font-weight: 400;
        }

        /* Enhanced category headers with underline accent */
        .category-title {
          font-size: 22px;
          font-weight: 700;
          color: #1e40af;
          margin: 40px 0 24px 0;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          position: relative;
          padding-bottom: 8px;
        }

        .category-title::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 60px;
          height: 3px;
          background: linear-gradient(90deg, #3b82f6, #1e40af);
          border-radius: 2px;
        }

        /* Enhanced services list with better spacing */
        .services-list {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        /* Enhanced service items with subtle hover effects */
        .service-item {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 20px;
          margin: 0;
          transition: all 0.2s ease;
          cursor: pointer;
        }

        .service-item:hover {
          background: #f1f5f9;
          border-color: #cbd5e1;
          transform: translateX(4px);
        }

        /* Enhanced service header with better alignment */
        .service-header {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          margin-bottom: 8px;
        }

        /* Enhanced service name typography */
        .service-name {
          font-size: 18px;
          font-weight: 600;
          color: #1e40af;
          margin: 0;
          line-height: 1.3;
        }

        /* Enhanced service prices with better styling */
        .service-prices {
          font-size: 16px;
          font-weight: 700;
          color: #1e40af;
          white-space: nowrap;
          background: #dbeafe;
          padding: 3px 8px;
          border-radius: 16px;
          border: 1px solid #bfdbfe;
        }

        /* Enhanced service details with better spacing */
        .service-details {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          gap: 16px;
        }

        /* Enhanced service description typography */
        .service-description {
          color: #64748b;
          font-size: 15px;
          margin: 0;
          line-height: 1.5;
          flex: 1;
        }

        /* Enhanced service duration with badge styling */
        .service-duration {
          color: #475569;
          font-size: 14px;
          white-space: nowrap;
          background: #e2e8f0;
          padding: 4px 8px;
          border-radius: 12px;
          font-weight: 500;
        }

        /* Enhanced price styling */
        .price-tnd {
          color: #1e40af;
          font-weight: 700;
        }

        .price-separator {
          color: #64748b;
          margin: 0 4px;
          font-weight: 400;
        }

        .price-eur {
          color: #1e40af;
          font-weight: 700;
        }

        /* Enhanced loading and empty states */
        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 200px;
          gap: 16px;
          color: #64748b;
          background: white;
          border-radius: 16px;
          padding: 40px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }

        .loading-spinner {
          border: 4px solid #f1f5f9;
          border-top: 4px solid #3b82f6;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        /* Added slideshow styles */
        .spa-slideshow {
          position: relative;
          max-width: 1000px;
          margin: 40px auto;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        }

        .slideshow-container {
          position: relative;
          width: 100%;
          height: 400px;
          overflow: hidden;
        }

        .slide {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          opacity: 0;
          transition: opacity 0.8s ease-in-out;
        }

        .slide.active {
          opacity: 1;
        }

        .slide img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        }

        .slideshow-nav {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(0, 0, 0, 0.5);
          color: white;
          border: none;
          width: 50px;
          height: 50px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          backdrop-filter: blur(10px);
          z-index: 10;
        }

        .slideshow-nav:hover {
          background: rgba(0, 0, 0, 0.7);
          transform: translateY(-50%) scale(1.1);
        }

        .slideshow-nav.prev {
          left: 20px;
        }

        .slideshow-nav.next {
          right: 20px;
        }

        .slideshow-dots {
          position: absolute;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 12px;
          z-index: 10;
        }

        .dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.5);
          cursor: pointer;
          transition: all 0.3s ease;
          border: 2px solid transparent;
        }

        .dot.active {
          background: white;
          transform: scale(1.2);
          border-color: rgba(59, 130, 246, 0.5);
        }

        .dot:hover {
          background: rgba(255, 255, 255, 0.8);
          transform: scale(1.1);
        }

        /* Mobile responsiveness for slideshow */
        @media (max-width: 768px) {
          .spa-slideshow {
            margin: 20px 16px;
            border-radius: 16px;
          }
          
          .slideshow-container {
            height: 250px;
          }
          
          .slideshow-nav {
            width: 40px;
            height: 40px;
          }
          
          .slideshow-nav.prev {
            left: 10px;
          }
          
          .slideshow-nav.next {
            right: 10px;
          }
          
          .slideshow-dots {
            bottom: 15px;
            gap: 8px;
          }
          
          .dot {
            width: 10px;
            height: 10px;
          }
        }

        /* RTL Support */
        .rtl {
          direction: rtl;
        }
        .rtl .language-selector {
          left: auto;
          right: 15px;
        }
        .rtl .language-dropdown {
          left: auto;
          right: 0;
          text-align: right;
        }
        .rtl .header-back-link {
          flex-direction: row-reverse;
        }
        .rtl .header-back-link svg {
          margin-left: 8px;
          margin-right: 0;
        }
        .rtl .welcome-banner h1 {
          text-align: right;
        }
        .rtl .welcome-banner p {
          text-align: right;
        }
        .rtl .empty-state {
          text-align: right;
        }
        .rtl .spa-categories {
          align-items: flex-end;
        }
        .rtl .spa-category {
          flex-direction: row-reverse;
        }
        .rtl .spa-category .category-title {
          text-align: right;
        }
        .rtl .services-list {
          flex-direction: column-reverse;
        }
        .rtl .service-item {
          flex-direction: row-reverse;
        }
        .rtl .service-header {
          flex-direction: row-reverse;
        }
        .rtl .service-header .service-name {
          margin-left: 0;
          margin-right: 8px;
        }
        .rtl .service-header .service-prices {
          margin-left: 0;
          margin-right: 8px;
        }
        .rtl .service-details {
          flex-direction: row-reverse;
        }
        .rtl .service-details .service-description {
          margin-left: 0;
          margin-right: 8px;
        }
        .rtl .service-details .service-duration {
          margin-left: 0;
          margin-right: 8px;
        }
        .rtl .footer-section {
          text-align: right;
        }
        .rtl .copyright {
          text-align: right;
        }
        .rtl .copyright a {
          margin-left: 0;
          margin-right: 5px;
        }
        .rtl .modal h2,
        .rtl .modal input,
        .rtl .modal-actions {
          text-align: right;
        }
        .rtl .modal-actions {
          flex-direction: row-reverse;
        }

        /* RTL support for slideshow */
        .rtl .slideshow-nav.prev {
          left: auto;
          right: 20px;
        }
        
        .rtl .slideshow-nav.next {
          right: auto;
          left: 20px;
        }
        
        @media (max-width: 768px) {
          .rtl .slideshow-nav.prev {
            left: auto;
            right: 10px;
          }
          
          .rtl .slideshow-nav.next {
            right: auto;
            left: 10px;
          }
        }
      `}</style>
      {/* Language Selector */}
      <div className="language-selector">
        <button className="language-toggle" onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}>
          <img
            src={getCurrentLanguage()?.flag || "/placeholder.svg"}
            alt={getCurrentLanguage()?.name}
            className="language-flag"
          />
          <span>{getCurrentLanguage()?.code.toUpperCase()}</span>
        </button>
        {showLanguageDropdown && (
          <div className="language-dropdown">
            {languages.map((lang) => (
              <div
                key={lang.code}
                className={`language-option ${currentLanguage === lang.code ? "active" : ""}`}
                onClick={(e) => {
                  e.stopPropagation()
                  changeLanguage(lang.code)
                }}
              >
                <img src={lang.flag || "/placeholder.svg"} alt={lang.name} className="flag-small" />
                <span>{lang.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <header className="app-header">
        <a href="/Home" className="header-back-link">
          <ArrowLeft className={currentLanguage === "ar" ? "transform scale-x-[-1]" : ""} width="24" height="24" />
          {t("back")}
        </a>
        <div className="logo-container">
          <img src="/images/logo2.png" alt="Novotel Logo" className="logo" />
        </div>
        <div></div> {/* Empty div for flex spacing */}
      </header>
      <main className="app-main">
        <div className="welcome-banner">
          <h1>
            {t("spa")}
            {t("wellness")}
          </h1>
          <p>{t("discoverSpa")}</p>
        </div>

        <div className="spa-slideshow">
          <div className="slideshow-container">
            {spaImages.map((image, index) => (
              <div key={index} className={`slide ${index === currentSlide ? "active" : ""}`}>
                <img
                  src={image || "/placeholder.svg"}
                  alt={`Spa image ${index + 1}`}
                  loading={index === 0 ? "eager" : "lazy"}
                />
              </div>
            ))}

            <button className="slideshow-nav prev" onClick={prevSlide} aria-label="Previous image">
              <ChevronLeft size={24} />
            </button>

            <button className="slideshow-nav next" onClick={nextSlide} aria-label="Next image">
              <ChevronRight size={24} />
            </button>

            <div className="slideshow-dots">
              {spaImages.map((_, index) => (
                <button
                  key={index}
                  className={`dot ${index === currentSlide ? "active" : ""}`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="content-container">
          {isLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>{t("loadingSpas")}</p>
            </div>
          ) : spas.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🔍</div>
              <h3>{t("noSpasFound")}</h3>
              <p>{t("comeBackSoonSpas")}</p>
            </div>
          ) : (
            <div className="spas-list">
              {spas.map((spa) => (
                <div key={spa._id} className="spa-container">
                  <h2 className="spa-name">{spa.name}</h2>
                  {spa.description && <p className="spa-description">{spa.description}</p>}

                  {spa.categories && spa.categories.length > 0 ? (
                    spa.categories.map((category, categoryIndex) => (
                      <div key={categoryIndex}>
                        <h3 className="category-title">{category.title}</h3>
                        <div className="services-list">
                          {category.services.map((service, serviceIndex) => (
                            <div key={serviceIndex} className="service-item">
                              <div className="service-header">
                                <h4 className="service-name">{service.name}</h4>
                                <div className="service-prices">
                                  {service.prices?.TND && <span className="price-tnd">{service.prices.TND}TND</span>}
                                  {service.prices?.TND && service.prices?.EUR && (
                                    <span className="price-separator"> / </span>
                                  )}
                                  {service.prices?.EUR && <span className="price-eur">{service.prices.EUR}€</span>}
                                </div>
                              </div>

                              <div className="service-details">
                                <p className="service-description">{service.description || ""}</p>
                                <span className="service-duration">{service.duration || ""}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div>
                      <h3 className="category-title">{t("defaultFeatures.massage")}</h3>
                      <div className="services-list">
                        <div className="service-item">
                          <div className="service-header">
                            <h4 className="service-name">{spa.name}</h4>
                          </div>
                          <div className="service-details">
                            <p className="service-description">{spa.description || t("noDescription")}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <footer className="app-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>{t("contact")}</h4>
            <p>+216 71 832 555</p>
            <p>H6145@accor.com</p>
          </div>
          <div className="footer-section">
            <h4>{t("address")}</h4>
            <p>{t("addressLine1")}</p>
            <p>{t("addressLine2")}</p>
          </div>
          <div className="footer-section">
            <h4>{t("reservations")}</h4>
            <p>+216 71 832 555</p>
            <p>H6145@accor.com</p>
          </div>
          <div className="footer-section">
            <h4>{t("wifi")}</h4>

            <p>
              {t("password")}: {t("availableAtReception")}
            </p>
          </div>
          <div className="footer-section">
            <h4>{t("followUs")}</h4>
            <div className="social-links">
              <a href="https://www.facebook.com/NovotelTunis" aria-label="Facebook">
                <Facebook width="24" height="24" />
              </a>
              <a href="https://www.instagram.com/novotel.tunis" aria-label="Instagram">
                <Instagram width="24" height="24" />
              </a>

            </div>
          </div>
        </div>
        <div className="copyright">
          <p>
            © {new Date().getFullYear()} Novotel Tunis. {t("allRightsReserved")}.
            <br />
            {t("createdBy")}{" "}
            <a href="https://www.itbafa.com" target="_blank" rel="noopener noreferrer">
              <img
                src="/images/itbafa_logo_white.png"
                alt="ITBAFA Logo"
                style={{ height: "20px", verticalAlign: "middle", marginLeft: "5px" }}
              />
            </a>
          </p>
        </div>
      </footer>
    </div>
  )
}

export default SpasClient
